/**
 * FestivalStage — feasts and festivals: four posts strung with lanterns and
 * bunting around the square, tables (feast), banners, a bonfire with a
 * flickering light and rising embers, and — festival only — fireworks that
 * launch every few seconds and burst overhead. Everything is scaled from the
 * catalog's crowdRadiusM so a hamlet feast and a city festival both fit.
 */
import * as THREE from 'three/webgpu';
import { glowTexture, flagTexture } from '../fx_textures.js';
import { FxSpriteBatch, type FxSprite } from '../fx_sprite_batch.js';
import { type Stage, type StageCtx, disposeGroup, makeBanner, waveCloth, propMaterial } from './StageBase.js';
import { ceremonyProp } from '../ceremony_props_loader.js';
import { mergeStaticMeshes } from '../../../lib/static_merge.js';

const LANTERN_COLORS = [0xffb347, 0xff6f61, 0xffd76a, 0x7fd3ff, 0xc77dff];
const FLAG_COLORS = [0xff6f61, 0xffd76a, 0x7fd3ff, 0x8cf7a0, 0xc77dff];

interface Ember { s: FxSprite; vy: number; ttl: number; life: number; ox: number; oz: number }
interface Spark { s: FxSprite; v: THREE.Vector3; ttl: number; life: number }
/** One burst is a flash + 26 sparks beside the next rocket; two never overlap (launches ≥ 2.5 s apart, sparks ≤ 1.7 s). */
const FIREWORK_CAP = 64;

export class FestivalStage implements Stage {
  readonly group = new THREE.Group();
  private lanterns: FxSprite[] = [];
  private flames: FxSprite[] = [];
  private embers: Ember[] = [];
  private sparks: Spark[] = [];
  private rocket: { s: FxSprite; vy: number; ttl: number } | null = null;
  private nextLaunch = 1.5;
  private fireGlow: FxSprite | null = null;
  // ONE DRAW PER STRING, not per lantern: every glow and pennant of the square used to be its own
  // THREE.Sprite (~115 render objects, +27 per burst). Batches are per SIDE so the transparent sort
  // still orders a near string in front of a far one.
  private lanternBatches: FxSpriteBatch[] = [];
  private fireBatch: FxSpriteBatch | null = null;
  private skyBatch: FxSpriteBatch | null = null;
  private banners: THREE.Group[] = [];
  private fireAt: THREE.Vector3;
  private bursts = 0;
  private generated = 0;
  private meshesBefore = 0;
  private meshesAfter = 0;

  constructor(private readonly ctx: StageCtx) {
    const { x, z, y, def } = ctx;
    const r = Math.max(5, def.crowdRadiusM);
    this.group.position.set(x, y, z);
    ctx.root.add(this.group);
    this.fireAt = new THREE.Vector3(0, 0.6, 0);
    const props = new Set(def.props);

    // Posts + strings: lanterns and bunting around the square.
    const posts: THREE.Vector3[] = [];
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const px = Math.cos(a) * r, pz = Math.sin(a) * r;
      const py = ctx.groundAt(x + px, z + pz) - y;
      // The generated lantern post (Hugging Face wave), its lantern arm turned
      // toward the square; the plain pole stands in until it lands.
      const genPost = ceremonyProp('lantern_post', Math.atan2(-px, -pz));
      if (genPost) { genPost.position.set(px, py, pz); this.group.add(genPost); this.generated++; }
      else {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 3.6, 6), propMaterial(0x6b5238, 0.9));
        post.position.set(px, py + 1.8, pz);
        this.group.add(post);
      }
      posts.push(new THREE.Vector3(px, py + 3.5, pz));
    }
    for (let i = 0; i < 4; i++) {
      const a = posts[i], b = posts[(i + 1) % 4];
      const n = Math.max(4, Math.round(a.distanceTo(b) / 1.3));
      const mid = a.clone().lerp(b, 0.5);
      // Pennants FIRST: two batches at one anchor draw in creation order, and the glow adds over the cloth.
      const flags = props.has('bunting') ? new FxSpriteBatch(flagTexture(), n - 1, false, mid) : null;
      const glows = props.has('lanterns') ? new FxSpriteBatch(glowTexture(), n - 1, true, mid) : null;
      for (let k = 1; k < n; k++) {
        const f = k / n;
        const sag = Math.sin(f * Math.PI) * 0.6;
        const p = a.clone().lerp(b, f); p.y -= sag;
        if (glows) {
          const sp = glows.add(LANTERN_COLORS[(i * 7 + k) % LANTERN_COLORS.length], 0.55, 1, p.x, p.y - 0.25, p.z);
          if (sp) this.lanterns.push(sp);
        }
        flags?.add(FLAG_COLORS[(i * 3 + k) % FLAG_COLORS.length], 0.32, 0.95, p.x, p.y - 0.08, p.z);
      }
      if (flags) { flags.commit(); this.group.add(flags.sprite); } // static: written once
      if (glows) { glows.commit(); this.group.add(glows.sprite); this.lanternBatches.push(glows); }
    }

    if (props.has('tables')) {
      const wood = propMaterial(0x8a5a2c, 0.85);
      const cloth = propMaterial(0xe9dcc3, 0.95);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.4;
        const tx = Math.cos(a) * r * 0.55, tz = Math.sin(a) * r * 0.55;
        const ty = ctx.groundAt(x + tx, z + tz) - y;
        // The generated feast table (Hugging Face wave), set with bowls and
        // platters, laid tangent to the ring; the trestle boards stand in until it lands.
        const genTable = ceremonyProp('feast_table', a + Math.PI / 2, 'x');
        if (genTable) { genTable.position.set(tx, ty, tz); this.group.add(genTable); this.generated++; continue; }
        const table = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.12, 0.9), wood);
        table.position.set(tx, ty + 0.78, tz);
        table.rotation.y = a + Math.PI / 2;
        const top = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.02, 0.8), cloth);
        top.position.set(tx, ty + 0.85, tz);
        top.rotation.y = a + Math.PI / 2;
        for (const s of [-1, 1]) {
          const leg = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.78, 0.7), wood);
          leg.position.set(tx + Math.cos(a + Math.PI / 2) * 1.3 * s, ty + 0.39, tz + Math.sin(a + Math.PI / 2) * 1.3 * s);
          leg.rotation.y = a + Math.PI / 2;
          this.group.add(leg);
        }
        this.group.add(table, top);
      }
    }

    if (props.has('banners')) {
      for (let i = 0; i < 2; i++) {
        const a = i === 0 ? Math.PI * 0.25 : Math.PI * 1.25;
        const bx = Math.cos(a) * r * 0.8, bz = Math.sin(a) * r * 0.8;
        const b = makeBanner(ctx.title, i === 0 ? 0xb5452f : 0x2f6db5);
        b.position.set(bx, ctx.groundAt(x + bx, z + bz) - y, bz);
        b.rotation.y = -a;
        this.group.add(b);
        this.banners.push(b);
      }
    }

    if (props.has('bonfire')) {
      // The generated stone fire pit with its stacked logs (Hugging Face wave)
      // under the flames; the log tepee + stone ring stand in until it lands.
      const genPit = ceremonyProp('fire_pit', 0);
      if (genPit) { this.group.add(genPit); this.generated++; }
      else {
        const logs = propMaterial(0x4a3422, 1);
        for (let i = 0; i < 5; i++) {
          const log = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 1.5, 6), logs);
          log.rotation.z = Math.PI / 2 - 0.35;
          log.rotation.y = (i / 5) * Math.PI * 2;
          log.position.y = 0.25;
          this.group.add(log);
        }
        const stones = propMaterial(0x6d6a66, 1);
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * Math.PI * 2;
          const st = new THREE.Mesh(new THREE.DodecahedronGeometry(0.22, 0), stones);
          st.position.set(Math.cos(a) * 1.1, 0.12, Math.sin(a) * 1.1);
          this.group.add(st);
        }
      }
      // Flames, the ground glow and the embers are all additive glow: one batch, one draw.
      const fire = new FxSpriteBatch(glowTexture(), 3 + 1 + 14, true, this.fireAt);
      this.fireBatch = fire;
      this.group.add(fire.sprite);
      for (let i = 0; i < 3; i++) {
        const f = fire.add(i === 0 ? 0xffe9a8 : i === 1 ? 0xff9a3c : 0xff5a1f, 1.2 + i * 0.5, 1, 0, 0.7 + i * 0.35, 0);
        if (f) this.flames.push(f);
      }
      // No PointLight (the light-set identity law) — a warm ground glow sprite stands in for the cast light.
      this.fireGlow = fire.add(0xff9a3c, 9, 0.35, 0, 0.3, 0);
      for (let i = 0; i < 14; i++) { const e = this.ember(true); if (e) this.embers.push(e); }
      fire.commit();
    }
    if (props.has('fireworks')) {
      this.skyBatch = new FxSpriteBatch(glowTexture(), FIREWORK_CAP, true, new THREE.Vector3(0, 12, 0));
      this.group.add(this.skyBatch.sprite);
    }
    // The posts, trestles, logs and ring stones never move and share five materials: one draw per material
    // instead of one per plank. The banners keep their own meshes (waveCloth animates the cloth).
    const r0 = mergeStaticMeshes(this.group, (m) => { for (let o: THREE.Object3D | null = m; o; o = o.parent) if (this.banners.includes(o as THREE.Group)) return true; return false; });
    this.meshesBefore = r0.before; this.meshesAfter = r0.after;
  }

  private ember(randomAge: boolean): Ember | null {
    const s = this.fireBatch?.add(0xffb35a, 0.12 + Math.random() * 0.1);
    if (!s) return null;
    const life = 1.8 + Math.random() * 1.6;
    const e: Ember = { s, vy: 1.2 + Math.random() * 1.2, ttl: randomAge ? Math.random() * life : life, life, ox: (Math.random() - 0.5) * 0.8, oz: (Math.random() - 0.5) * 0.8 };
    s.x = e.ox; s.y = 0.6 + (life - e.ttl) * e.vy; s.z = e.oz;
    return e;
  }

  update(t: number, dt: number): void {
    for (let i = 0; i < this.lanterns.length; i++) {
      const l = this.lanterns[i];
      l.size = 0.55 * (0.85 + Math.sin(t * 2.3 + i * 1.7) * 0.15); // flicker by scale
      l.x += Math.sin(t * 1.1 + i) * 0.0015;
    }
    for (let i = 0; i < this.flames.length; i++) {
      const f = this.flames[i];
      const k = 1 + Math.sin(t * (9 + i * 2.3) + i) * 0.18 + Math.sin(t * 17 + i * 4) * 0.08;
      f.size = (1.2 + i * 0.5) * k;
      f.y = 0.7 + i * 0.35 + Math.sin(t * 6 + i) * 0.08;
    }
    if (this.fireGlow) this.fireGlow.size = 9 + Math.sin(t * 11) * 0.8 + Math.sin(t * 23.7) * 0.5;
    for (const e of this.embers) {
      e.ttl -= dt;
      if (e.ttl <= 0) { e.ttl = e.life; e.ox = (Math.random() - 0.5) * 0.8; e.oz = (Math.random() - 0.5) * 0.8; }
      const age = e.life - e.ttl;
      e.s.x = e.ox + Math.sin(t * 2 + age * 3) * 0.25; e.s.y = 0.6 + age * e.vy; e.s.z = e.oz + Math.cos(t * 1.7 + age * 2.5) * 0.25;
      e.s.size = 0.18 * Math.max(0.01, 1 - age / e.life); // fade by scale
    }
    this.banners.forEach((b, i) => waveCloth(b, t, i * 1.3));
    if (this.skyBatch) this.fireworks(t, dt);
    for (const b of this.lanternBatches) b.commit();
    this.fireBatch?.commit();
    this.skyBatch?.commit();
  }

  private fireworks(_t: number, dt: number): void {
    const sky = this.skyBatch;
    if (!sky) return;
    this.nextLaunch -= dt;
    if (!this.rocket && this.nextLaunch <= 0) {
      const s = sky.add(0xfff1c0, 0.3, 1, (Math.random() - 0.5) * 6, 1, (Math.random() - 0.5) * 6);
      if (s) this.rocket = { s, vy: 22 + Math.random() * 6, ttl: 0.9 + Math.random() * 0.3 };
      this.nextLaunch = 2.5 + Math.random() * 3;
    }
    if (this.rocket) {
      this.rocket.ttl -= dt;
      this.rocket.s.y += this.rocket.vy * dt;
      this.rocket.vy -= 6 * dt;
      if (this.rocket.ttl <= 0) {
        const ax = this.rocket.s.x, ay = this.rocket.s.y, az = this.rocket.s.z;
        sky.remove(this.rocket.s);
        this.rocket = null;
        this.bursts++;
        const tint = LANTERN_COLORS[this.bursts % LANTERN_COLORS.length];
        const flash = sky.add(0xffffff, 3, 1, ax, ay, az);
        if (flash) this.sparks.push({ s: flash, v: new THREE.Vector3(), ttl: 0.12, life: 0.12 });
        for (let i = 0; i < 26; i++) {
          const s = sky.add(i % 3 ? tint : 0xffffff, 0.35, 1, ax, ay, az);
          if (!s) break;
          const v = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(7 + Math.random() * 5);
          this.sparks.push({ s, v, ttl: 1.2 + Math.random() * 0.5, life: 1.7 });
        }
      }
    }
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const sp = this.sparks[i];
      sp.ttl -= dt;
      if (sp.ttl <= 0) { sky.remove(sp.s); this.sparks.splice(i, 1); continue; }
      sp.v.y -= 4 * dt;
      sp.s.x += sp.v.x * dt; sp.s.y += sp.v.y * dt; sp.s.z += sp.v.z * dt;
      sp.s.size = 0.35 * Math.min(1, sp.ttl / sp.life * 1.6); // fade by scale
    }
  }

  describe(): Record<string, number | string | boolean> {
    let spriteDraws = 0;
    this.group.traverse((o) => { if ((o as THREE.Sprite).isSprite && o.visible) spriteDraws++; });
    return { family: 'festival', lanterns: this.lanterns.length, flames: this.flames.length, embers: this.embers.length, banners: this.banners.length, bursts: this.bursts, fireworks: this.ctx.def.props.includes('fireworks'), lights: 0, generated: this.generated, sparks: this.sparks.length, spriteDraws, dropped: this.skyBatch?.dropped ?? 0, meshesBuilt: this.meshesBefore, meshDraws: this.meshesAfter };
  }

  dispose(): void {
    disposeGroup(this.group); // the batches are children: their (unshared) materials go with the group
  }
}
